import { Component, Inject, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { TaxonomyApiService } from '../../services/api-services/taxonomy-api.service';
import { NotificationService } from '../../services/notification.service';

export interface CreateDatasetData {
  selectedContactIds: string[];
}

@Component({
  selector: 'app-create-dataset-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule],
  providers: [TaxonomyApiService, NotificationService],
  template: `
    <div class="dialog-container">
      <form [formGroup]="datasetForm" (ngSubmit)="onSubmit()">
        <div class="form-group">
          <label for="dataset_label">Label</label>
          <input
            id="dataset_label"
            type="text"
            formControlName="dataset_label"
            placeholder="Enter label"
            required
            (input)="onLabelInput()"
          />
          <div *ngIf="labelError" class="error-message">{{ labelError }}</div>
        </div>

        <div class="form-group">
          <label for="dataset_description">Description</label>
          <textarea
            id="dataset_description"
            formControlName="dataset_description"
            placeholder="Enter description (optional)"
            rows="4"
          ></textarea>
        </div>

        <div class="form-actions">
          <button type="button" class="cancel-btn" (click)="onCancel()" [disabled]="isLoading">Cancel</button>
          <button
            type="submit"
            class="create-btn"
            [disabled]="!datasetForm.get('dataset_label')?.value || isLoading || labelError"
          >
            {{ isLoading ? 'Create' : 'Create' }}
          </button>
        </div>
      </form>
    </div>
  `,
  styles: [
    `
      .dialog-container {
        background: white;
        border-radius: 8px;
        padding: 16px;
        width: 280px;
      }

      .dialog-title {
        margin: 0 0 16px 0;
        font-family: 'Lexend', sans-serif;
        font-size: 18px;
        font-weight: 600;
        color: #333;
      }

      .form-group {
        margin-bottom: 16px;
      }

      .form-group label {
        display: block;
        margin-bottom: 6px;
        font-family: 'Lexend', sans-serif;
        font-size: 14px;
        font-weight: 500;
        color: #333;
      }

      .form-group label[for='dataset_label']::after {
        content: ' *';
        color: #ff4444;
      }

      .form-group input,
      .form-group textarea {
        width: 248px;
        padding: 10px;
        border: 1px solid #e1e5e9;
        border-radius: 4px;
        font-family: 'Lexend', sans-serif;
        font-size: 14px;
        color: #333;
        background: white;
        box-sizing: border-box;
      }

      .form-group input:focus,
      .form-group textarea:focus {
        outline: none;
        border-color: #534ad8;
      }

      .form-group input::placeholder,
      .form-group textarea::placeholder {
        color: #999;
      }

      .form-group textarea {
        min-height: 70px;
        resize: vertical;
      }

      .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        margin-top: 20px;
        width: 248px;
      }

      .form-actions button {
        display: flex;
        width: 55px;
        height: 25px;
        padding: 5px 8px;
        justify-content: center;
        align-items: center;
        gap: 5px;
        cursor: pointer;
        border: none;
      }

      .cancel-btn {
        display: flex;
        height: 25px;
        padding: 4px 8px;
        align-items: center;
        gap: 4px;
        border-radius: 4px;
        background: #ffffff;
      }

      .cancel-btn:hover {
        border-radius: 4px;
        background: var(--background-variant-1-f-5-f-7-fa, #f5f7fa);
      }

      .create-btn {
        display: flex;
        width: 55px;
        height: 25px;
        padding: 5px 8px;
        justify-content: center;
        align-items: center;
        gap: 5px;
        border-radius: 4px;
        background: var(--primary-varient-2534-ad-8, #534ad8);

        color: var(--background-variant-0-ffffff, #fff);

        /* Caption/Caption - Regular - 12|Auto */
        font-family: var(--Typeface-Family-Caption, Lexend);
        font-size: var(--Typeface-Font-size-2xs, 12px);
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        letter-spacing: var(--Typeface-Letter-spacing-0, 0);
        border: none;
        cursor: pointer;
      }

      .create-btn:hover:not(:disabled) {
        background: #534ad8;
      }

      .create-btn:disabled {
        background: #bfccd9;
        cursor: not-allowed;
      }

      .error-message {
        margin-top: 8px;
        color: var(--alerts-warning-ff-4-a-4-a, #ff4a4a);

        /* Caption/Caption - Regular - 12|Auto */
        font-family: var(--Typeface-Family-Caption, Lexend);
        font-size: var(--Typeface-Font-size-2xs, 12px);
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        letter-spacing: var(--Typeface-Letter-spacing-0, 0);
      }
    `,
  ],
})
export class CreateDatasetDialogComponent implements OnInit {
  datasetForm: FormGroup;
  selectedContactIds: string[];
  @Output() datasetCreated = new EventEmitter<void>();
  isLoading: boolean = false;
  labelError: string = '';
  existingLabels: string[] = [];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<CreateDatasetDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CreateDatasetData,
    private taxonomyApiService: TaxonomyApiService,
    private notificationService: NotificationService
  ) {
    this.selectedContactIds = data.selectedContactIds;
    this.datasetForm = this.fb.group({
      dataset_label: ['', Validators.required],
      dataset_description: [''],
    });
  }

  ngOnInit(): void {
    this.loadExistingLabels();
  }

  loadExistingLabels(): void {
    this.taxonomyApiService.getUserDatasetLabels().subscribe({
      next: (response) => {
        if (response?.success && response?.data) {
          this.existingLabels = response.data;
        }
      },
      error: (error) => {
        console.error('Failed to fetch existing labels:', error);
      },
    });
  }

  onLabelInput(): void {
    const labelValue = this.datasetForm.get('dataset_label')?.value?.trim();
    this.labelError = '';

    if (labelValue && this.existingLabels.includes(labelValue)) {
      this.labelError = 'Label already exists. Try another name.';
    }
  }

  onSubmit(): void {
    if (this.datasetForm.valid && !this.labelError) {
      this.isLoading = true;

      const payload = {
        label: this.datasetForm.value.dataset_label.trim(),
        description: this.datasetForm.value.dataset_description?.trim() || '',
        contactIds: this.selectedContactIds,
      };

      // Call the API to create dataset
      this.taxonomyApiService.createDataset(payload).subscribe({
        next: (response) => {
          this.notificationService.success('Success', 'Dataset created');

          // Emit event to notify parent component
          this.datasetCreated.emit();

          this.dialogRef.close(payload);
        },
        error: (error) => {
          this.notificationService.error('Error', 'Something went wrong. Please try again later.');
          this.isLoading = false;
        },
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
